import type {
  AllocationConstraint,
  AllocationExceptionRequest,
  AssignMemberRole,
  // Budget types
  BudgetCategory,
  BudgetExceptionRequest,
  BudgetRule,
  BudgetTemplate,
  BudgetValidationResult,
  BulkApproveEarningsResult,
  BulkPayEarningsResult,
  BulkUnapproveEarningsResult,
  CapacityForecastPoint,
  CompensationBreakdown,
  CompensationType,
  CreateBenefitTemplate,
  CreateBudgetCategory,
  CreateBudgetExceptionRequest,
  CreateBudgetTemplate,
  CreateForecastScenario,
  CreateJobRole,
  CreateMemberAllocation,
  CreateMemberBenefit,
  CreateMemberCompensation,
  CreateMemberRate,
  CreateMemberSkill,
  CreateMemberTaxOverride,
  CreatePayrollTaxItem,
  CreateProjectBudget,
  CreateProjectPhase,
  CreateRateCard,
  CreateRateCardEntry,
  EffectiveRateResult,
  EnrichedMemberAllocation,
  EntityJobRoleSelection,
  ForecastScenario,
  ForecastTimeline,
  Granularity,
  JobRole,
  MemberAllocation,
  MemberAvailabilityCheck,
  MemberBenefit,
  MemberCompensation,
  MemberEarnings,
  // Role assignment types
  MemberOrganizationRole,
  MemberRate,
  MemberSkill,
  MemberTaxOverride,
  MemberUtilization,
  OrganizationBenefitTemplate,
  // Compensation types
  OrganizationPayrollConfig,
  PayPeriodFrequency,
  PayrollCsvFormat,
  PayrollRunProcessResult,
  PayrollRunSummary,
  PayrollTaxItem,
  ProjectBudget,
  ProjectForecastSnapshot,
  ProjectMemberJobRole,
  ProjectOverviewSummaryResponse,
  ProjectPhase,
  ProjectStaffingResponse,
  ProjectTimeEntriesResponse,
  // Rate Card & Role types
  RateCard,
  ResourceRequest,
  RevenueForecastPoint,
  SetEntityJobRoleSelections,
  SetProjectMemberJobRoles,
  WorkloadSummary as SharedWorkloadSummary,
  UpdateBenefitTemplate,
  UpdateBudgetCategory,
  UpdateBudgetTemplate,
  UpdateForecastScenario,
  UpdateJobRole,
  UpdateMemberAllocation,
  UpdateMemberBenefit,
  UpdateMemberCompensation,
  UpdateMemberRate,
  UpdateMemberSkill,
  UpdateMemberTaxOverride,
  UpdatePayrollConfig,
  UpdatePayrollTaxItem,
  UpdateRateCard,
  UtilizationForecastPoint,
  WorkloadAvailabilitySummary,
  WorkloadTargetType,
  WorkloadWeekPoint,
} from '@scrambl/shared'

import { apiClient, emptyResponseToNull } from './api-client'
import { requestBlob, requestBlobResponse } from './base-api'
import { scheduleApi } from './schedule.api'

// Member Rate API
// Backend: /member-resources/members/:memberId/rates (get, create), /member-resources/rates/:rateId (update, delete)
export const memberRateApi = {
  getAllForOrg: async (): Promise<MemberRate[]> => {
    return apiClient('/member-resources/rates', { method: 'GET' })
  },

  getByMember: async (memberId: string): Promise<MemberRate[]> => {
    return apiClient(`/member-resources/members/${memberId}/rates`, {
      method: 'GET',
    })
  },

  getEffectiveRate: async (params: {
    memberId: string
    entityId?: string
    projectId?: string
    taskId?: string
    roleId?: string
    date?: string
  }): Promise<MemberRate | null> => {
    const queryParams = new URLSearchParams()
    if (params.entityId) queryParams.append('entityId', params.entityId)
    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.taskId) queryParams.append('taskId', params.taskId)
    if (params.roleId) queryParams.append('roleId', params.roleId)
    if (params.date) queryParams.append('date', params.date)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/members/${params.memberId}/rates/effective${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  create: async (
    memberId: string,
    data: Omit<CreateMemberRate, 'memberResourceProfileId' | 'organizationId'>,
    options?: { replaceExisting?: boolean },
  ): Promise<MemberRate> => {
    return apiClient(`/member-resources/members/${memberId}/rates`, {
      method: 'POST',
      body: { ...data, replaceExisting: options?.replaceExisting },
    })
  },

  update: async (
    rateId: string,
    data: UpdateMemberRate,
    options?: { replaceExisting?: boolean },
  ): Promise<MemberRate> => {
    return apiClient(`/member-resources/rates/${rateId}`, {
      method: 'PUT',
      body: { ...data, replaceExisting: options?.replaceExisting },
    })
  },

  delete: async (
    rateId: string,
  ): Promise<{ action: 'deleted' | 'deactivated' }> => {
    return apiClient(`/member-resources/rates/${rateId}`, { method: 'DELETE' })
  },

  checkRateChangeImpact: async (data: {
    triggeredBy: string
    memberId?: string
    rateCardId?: string
    projectId?: string
    entityId?: string
    roleId?: string
  }): Promise<{ draftCount: number; submittedCount: number }> => {
    return apiClient('/member-resources/rates/check-impact', {
      method: 'POST',
      body: data,
    })
  },

  propagateRateChanges: async (data: {
    triggeredBy: string
    memberId?: string
    rateCardId?: string
    projectId?: string
    entityId?: string
    roleId?: string
    statusFilter?: ('DRAFT' | 'SUBMITTED' | 'CHANGES_REQUESTED' | 'REJECTED')[]
  }): Promise<{
    updated: number
    skipped: number
    failed: Array<{
      entryId: string
      reason: string
      memberName: string
      projectName: string
      date: string
    }>
  }> => {
    return apiClient('/member-resources/rates/propagate', {
      method: 'POST',
      body: data,
    })
  },
}

// Member Allocation API
// Backend: /member-resources/allocations (CRUD), /member-resources/allocations/conflicts

// The conflicts endpoint emits per-day rows ({conflictDate, totalAllocation,
// allocations: [{id, percent}]}); the period/totalPercent fields only exist on
// the older shape, so everything stays optional except allocation ids.
export interface AllocationConflict {
  type?: string
  memberId?: string | null
  memberName?: string | null
  conflictDate?: string | Date | null
  totalAllocation?: number | null
  period?: { start: Date | string; end: Date | string } | null
  totalPercent?: number | null
  totalHours?: number | null
  weeklyCapacity?: number | null
  overallocation?: number | null
  allocations: Array<{
    id: string
    percent?: number | string | null
    allocationPercent?: number | string | null
  }>
}

export const memberAllocationApi = {
  getAll: async (filters?: {
    memberId?: string
    projectId?: string
    entityId?: string
    dealId?: string
    status?: string
    startDate?: string
    endDate?: string
    includeEnrichment?: boolean
  }): Promise<{ data: EnrichedMemberAllocation[]; total: number }> => {
    const params = new URLSearchParams()
    if (filters?.memberId) params.append('memberId', filters.memberId)
    if (filters?.projectId) params.append('projectId', filters.projectId)
    if (filters?.entityId) params.append('entityId', filters.entityId)
    if (filters?.dealId) params.append('dealId', filters.dealId)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.startDate) params.append('startDate', filters.startDate)
    if (filters?.endDate) params.append('endDate', filters.endDate)
    if (filters?.includeEnrichment) params.append('include', 'enrichment')

    const response = await apiClient<
      | EnrichedMemberAllocation[]
      | { data: EnrichedMemberAllocation[]; total: number }
    >(`/member-resources/allocations?${params.toString()}`, { method: 'GET' })

    // Handle both array and wrapped response formats
    if (Array.isArray(response)) {
      return { data: response, total: response.length }
    }
    return response
  },

  getByMember: async (memberId: string): Promise<MemberAllocation[]> => {
    // Use getAll with memberId filter since backend doesn't have /member/:memberId route
    const result = await memberAllocationApi.getAll({ memberId })
    return result.data
  },

  create: async (
    data: Omit<CreateMemberAllocation, 'organizationId'>,
  ): Promise<MemberAllocation> => {
    return apiClient('/member-resources/allocations', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    allocationId: string,
    data: UpdateMemberAllocation,
  ): Promise<MemberAllocation> => {
    return apiClient(`/member-resources/allocations/${allocationId}`, {
      method: 'PUT',
      body: data,
    })
  },

  delete: async (allocationId: string): Promise<void> => {
    return apiClient(`/member-resources/allocations/${allocationId}`, {
      method: 'DELETE',
    })
  },

  detectConflicts: async (memberId?: string): Promise<AllocationConflict[]> => {
    const queryParams = new URLSearchParams()
    if (memberId) queryParams.append('memberId', memberId)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/allocations/conflicts${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  // Plan 4 G.1: allocation state transition endpoints
  transition: async (
    allocationId: string,
    body: {
      status: string
      reason?: string | null
      staffingDecisionId?: string | null
    },
  ): Promise<{
    allocation: { id: string; status: string }
    history: { id: string; action: string; changedAt: string }
  }> => {
    return apiClient(
      `/member-resources/allocations/${allocationId}/transition`,
      { method: 'POST', body },
    )
  },

  getHistory: async (
    allocationId: string,
  ): Promise<{
    history: Array<{
      id: string
      action: string
      previousValues: Record<string, unknown> | null
      newValues: Record<string, unknown> | null
      reason: string | null
      changedBy: { id: string; name: string } | null
      changedAt: string
      staffingDecisionId: string | null
    }>
  }> => {
    return apiClient(`/member-resources/allocations/${allocationId}/history`, {
      method: 'GET',
    })
  },

  bulkTransition: async (body: {
    allocationIds: string[]
    transition: { status: string; reason?: string | null }
    staffingDecisionId?: string | null
  }): Promise<{
    succeeded: string[]
    failed: { id: string; error: string; validNext?: string[] }[]
  }> => {
    return apiClient('/member-resources/allocations/bulk', {
      method: 'PATCH',
      body,
    })
  },
}

// Skill type for catalog
export interface Skill {
  id: string
  organizationId: string
  name: string
  category: string
  description?: string | null
  createdAt: Date
  updatedAt: Date
}

// Extended MemberSkill with member info for organization-wide queries
export interface MemberSkillWithMember extends MemberSkill {
  skill: Skill
  member: {
    id: string
    user: {
      id: string
      firstName: string | null
      lastName: string | null
      displayName: string | null
      email: string
      image: string | null
    }
  }
}

// Member Skill API
// Backend: /member-resources/members/:memberId/skills (CRUD), /member-resources/skills/catalog
export const memberSkillApi = {
  getByMember: async (memberId: string): Promise<MemberSkill[]> => {
    return apiClient(`/member-resources/members/${memberId}/skills`, {
      method: 'GET',
    })
  },

  // Get all member skills for the organization (for skills overview)
  getAllForOrganization: async (): Promise<MemberSkillWithMember[]> => {
    return apiClient('/member-resources/skills/all', { method: 'GET' })
  },

  findBySkills: async (
    skillRequirements: Array<{
      skillId: string
      minProficiency?: number
    }>,
  ): Promise<
    Array<{
      member: any
      skills: MemberSkill[]
      matchScore: number
    }>
  > => {
    return apiClient('/member-resources/skills/find', {
      method: 'POST',
      body: { skillRequirements },
    })
  },

  // Skills Catalog
  getCatalog: async (): Promise<Skill[]> => {
    return apiClient('/member-resources/skills/catalog', { method: 'GET' })
  },

  createSkill: async (data: {
    name: string
    category: string
    description?: string
  }): Promise<Skill> => {
    return apiClient('/member-resources/skills/catalog', {
      method: 'POST',
      body: data,
    })
  },

  updateSkill: async (
    skillId: string,
    data: { name?: string; category?: string; description?: string },
  ): Promise<Skill> => {
    return apiClient(`/member-resources/skills/catalog/${skillId}`, {
      method: 'PUT',
      body: data,
    })
  },

  deleteSkill: async (skillId: string): Promise<void> => {
    return apiClient(`/member-resources/skills/catalog/${skillId}`, {
      method: 'DELETE',
    })
  },

  add: async (
    memberId: string,
    data: Omit<CreateMemberSkill, 'memberId'>,
  ): Promise<MemberSkill> => {
    return apiClient(`/member-resources/members/${memberId}/skills`, {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    memberId: string,
    skillId: string,
    data: UpdateMemberSkill,
  ): Promise<MemberSkill> => {
    return apiClient(
      `/member-resources/members/${memberId}/skills/${skillId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
  },

  remove: async (memberId: string, skillId: string): Promise<void> => {
    return apiClient(
      `/member-resources/members/${memberId}/skills/${skillId}`,
      { method: 'DELETE' },
    )
  },
}

// Member Utilization API
// Backend: /member-resources/members/:memberId/utilization, /member-resources/utilization/team
export const memberUtilizationApi = {
  getByMember: async (
    memberId: string,
    params?: {
      periodType?: 'DAY' | 'WEEK' | 'MONTH'
      startDate?: string
      endDate?: string
    },
  ): Promise<MemberUtilization[]> => {
    const queryParams = new URLSearchParams()
    if (params?.periodType) queryParams.append('periodType', params.periodType)
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/members/${memberId}/utilization${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  getTeamUtilization: async (params?: {
    startDate?: string
    endDate?: string
  }): Promise<MemberUtilization[]> => {
    const queryParams = new URLSearchParams()
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/utilization/team${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  calculate: async (params: {
    startDate: string
    endDate: string
    memberId?: string
  }): Promise<{ message: string }> => {
    return apiClient('/member-resources/utilization/calculate', {
      method: 'POST',
      body: params,
    })
  },
}

// Member Earnings API
// Backend: /member-resources/earnings/me, /member-resources/members/:memberId/earnings, etc.
export const memberEarningsApi = {
  getByMember: async (
    memberId: string,
    params?: {
      startDate?: string
      endDate?: string
      status?: string
    },
  ): Promise<MemberEarnings[]> => {
    const queryParams = new URLSearchParams()
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.status) queryParams.append('status', params.status)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/members/${memberId}/earnings${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  getMyEarnings: async (params?: {
    startDate?: string
    endDate?: string
    status?: string
  }): Promise<MemberEarnings[]> => {
    const queryParams = new URLSearchParams()
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.status) queryParams.append('status', params.status)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/earnings/me${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  getPending: async (): Promise<MemberEarnings[]> => {
    return apiClient('/member-resources/earnings/pending', { method: 'GET' })
  },

  getSummary: async (): Promise<{
    byStatus: Record<string, { count: number; netEarnings: number }>
    totalCount: number
    totalNetEarnings: number
    totalHours: number
    billableHours: number
  }> => {
    return apiClient('/member-resources/earnings/summary', { method: 'GET' })
  },

  getAll: async (params?: {
    page?: number
    limit?: number
    startDate?: string
    endDate?: string
    status?: string | string[]
    memberId?: string | string[]
    employmentType?: string
    search?: string
    sortBy?: string
    sortDir?: string
  }): Promise<import('@scrambl/shared').PaginatedResponse<MemberEarnings>> => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', String(params.page))
    if (params?.limit) queryParams.append('limit', String(params.limit))
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    const statusVal = Array.isArray(params?.status)
      ? params.status.join(',')
      : params?.status
    if (statusVal) queryParams.append('status', statusVal)
    const memberIdVal = Array.isArray(params?.memberId)
      ? params.memberId.join(',')
      : params?.memberId
    if (memberIdVal) queryParams.append('memberId', memberIdVal)
    if (params?.employmentType)
      queryParams.append('employmentType', params.employmentType)
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortDir) queryParams.append('sortDir', params.sortDir)
    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/earnings${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  calculate: async (
    memberId: string,
    data: {
      periodStart: string
      periodEnd: string
    },
  ): Promise<MemberEarnings> => {
    return apiClient(
      `/member-resources/members/${memberId}/earnings/calculate`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  approve: async (earningsId: string): Promise<MemberEarnings> => {
    return apiClient(`/member-resources/earnings/${earningsId}/approve`, {
      method: 'PUT',
    })
  },

  unapprove: async (earningsId: string): Promise<MemberEarnings> => {
    return apiClient(`/member-resources/earnings/${earningsId}/unapprove`, {
      method: 'PUT',
    })
  },

  markAsPaid: async (
    earningsId: string,
    paymentRef: string,
    paidAt?: string,
  ): Promise<MemberEarnings> => {
    return apiClient(`/member-resources/earnings/${earningsId}/mark-paid`, {
      method: 'PUT',
      body: { paymentRef, paidAt },
    })
  },

  exportPdf: async (earningsId: string): Promise<Blob> => {
    return requestBlob(`/member-resources/earnings/${earningsId}/pdf`, {
      acceptType: 'application/pdf',
    })
  },
}

// Payroll Run API
// Backend: /member-resources/earnings/bulk/*, /earnings/payroll-run/summary, /export/payroll/*
export const payrollRunApi = {
  process: async (body: {
    periodStart: string
    periodEnd: string
    includeZeroHours?: boolean
    department?: string
    employmentType?: string
  }): Promise<PayrollRunProcessResult> => {
    return apiClient('/member-resources/earnings/bulk/process', {
      method: 'POST',
      body,
    })
  },

  bulkApprove: async (body: {
    earningsIds?: string[]
    periodStart?: string
    periodEnd?: string
  }): Promise<BulkApproveEarningsResult> => {
    return apiClient('/member-resources/earnings/bulk/approve', {
      method: 'POST',
      body,
    })
  },

  bulkPay: async (body: {
    paymentRef: string
    paidAt: string
    earningsIds?: string[]
    periodStart?: string
    periodEnd?: string
  }): Promise<BulkPayEarningsResult> => {
    return apiClient('/member-resources/earnings/bulk/pay', {
      method: 'POST',
      body,
    })
  },

  bulkUnapprove: async (body: {
    earningsIds?: string[]
    periodStart?: string
    periodEnd?: string
  }): Promise<BulkUnapproveEarningsResult> => {
    return apiClient('/member-resources/earnings/bulk/unapprove', {
      method: 'POST',
      body,
    })
  },

  getRunSummary: async (
    periodStart: string,
    periodEnd: string,
  ): Promise<PayrollRunSummary> => {
    const queryParams = new URLSearchParams({ periodStart, periodEnd })
    return apiClient(
      `/member-resources/earnings/payroll-run/summary?${queryParams.toString()}`,
      { method: 'GET' },
    )
  },

  exportCsv: async (params: {
    periodStart: string
    periodEnd: string
    format: PayrollCsvFormat
    status?: string
  }): Promise<{ blob: Blob; filename: string }> => {
    const queryParams = new URLSearchParams({
      periodStart: params.periodStart,
      periodEnd: params.periodEnd,
      format: params.format,
    })
    if (params.status) queryParams.append('status', params.status)

    const blob = await requestBlob(
      `/member-resources/export/payroll/csv?${queryParams.toString()}`,
      { acceptType: 'text/csv' },
    )

    return {
      blob,
      filename: `Payroll_${params.format}_${params.periodStart}_${params.periodEnd}.csv`,
    }
  },

  exportPdf: async (params: {
    periodStart: string
    periodEnd: string
    status?: string
  }): Promise<{ blob: Blob; filename: string }> => {
    const queryParams = new URLSearchParams({
      periodStart: params.periodStart,
      periodEnd: params.periodEnd,
    })
    if (params.status) queryParams.append('status', params.status)

    const blob = await requestBlob(
      `/member-resources/export/payroll/pdf?${queryParams.toString()}`,
      { acceptType: 'application/pdf' },
    )

    return {
      blob,
      filename: `Payroll_Report_${params.periodStart}_${params.periodEnd}.pdf`,
    }
  },
}

// Forecast API
// Backend: /member-resources/forecast/capacity, /forecast/revenue, etc.
export const forecastApi = {
  getCapacityForecast: async (params: {
    startDate: string
    endDate: string
    granularity?: Granularity
  }): Promise<CapacityForecastPoint[]> => {
    const queryParams = new URLSearchParams()
    queryParams.append('startDate', params.startDate)
    queryParams.append('endDate', params.endDate)
    if (params.granularity)
      queryParams.append('granularity', params.granularity)

    return apiClient(
      `/member-resources/forecast/capacity?${queryParams.toString()}`,
      {
        method: 'GET',
      },
    )
  },

  getRevenueForecast: async (params: {
    startDate: string
    endDate: string
    granularity?: Granularity
  }): Promise<RevenueForecastPoint[]> => {
    const queryParams = new URLSearchParams()
    queryParams.append('startDate', params.startDate)
    queryParams.append('endDate', params.endDate)
    if (params.granularity)
      queryParams.append('granularity', params.granularity)

    return apiClient(
      `/member-resources/forecast/revenue?${queryParams.toString()}`,
      {
        method: 'GET',
      },
    )
  },

  getUtilizationForecast: async (params: {
    startDate: string
    endDate: string
    granularity?: Granularity
  }): Promise<UtilizationForecastPoint[]> => {
    const queryParams = new URLSearchParams()
    queryParams.append('startDate', params.startDate)
    queryParams.append('endDate', params.endDate)
    if (params.granularity)
      queryParams.append('granularity', params.granularity)

    return apiClient(
      `/member-resources/forecast/utilization?${queryParams.toString()}`,
      {
        method: 'GET',
      },
    )
  },

  getTimeline: async (params?: {
    months?: number
    granularity?: Granularity
    entityIds?: string[]
    projectIds?: string[]
    from?: string
    to?: string
  }): Promise<ForecastTimeline> => {
    const queryParams = new URLSearchParams()
    if (params?.months) queryParams.append('months', params.months.toString())
    if (params?.granularity)
      queryParams.append('granularity', params.granularity)
    if (params?.entityIds?.length)
      queryParams.append('entityIds', params.entityIds.join(','))
    if (params?.projectIds?.length)
      queryParams.append('projectIds', params.projectIds.join(','))
    if (params?.from) queryParams.append('from', params.from)
    if (params?.to) queryParams.append('to', params.to)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/forecast/timeline${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  getLayeredTimeline: async (params?: {
    months?: number
    granularity?: Granularity
    entityIds?: string[]
    projectIds?: string[]
    from?: string
    to?: string
  }): Promise<{
    confirmed: ForecastTimeline
    tentative: ForecastTimeline
    placeholder: ForecastTimeline
  }> => {
    const queryParams = new URLSearchParams()
    if (params?.months) queryParams.append('months', params.months.toString())
    if (params?.granularity)
      queryParams.append('granularity', params.granularity)
    if (params?.entityIds?.length)
      queryParams.append('entityIds', params.entityIds.join(','))
    if (params?.projectIds?.length)
      queryParams.append('projectIds', params.projectIds.join(','))
    if (params?.from) queryParams.append('from', params.from)
    if (params?.to) queryParams.append('to', params.to)

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/forecast/timeline/layered${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },
}

// Scenario API
// Backend: /member-resources/scenarios (CRUD)
export interface ScenarioListParams {
  page?: number
  limit?: number
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'status'
  sortOrder?: 'asc' | 'desc'
  search?: string
  includeArchived?: boolean
  status?: string
  flaggedOnly?: boolean
}

export const scenarioApi = {
  getPage: async (
    params: ScenarioListParams = {},
  ): Promise<import('@scrambl/shared').PaginatedResponse<ForecastScenario>> => {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', String(params.page))
    if (params.limit) queryParams.append('limit', String(params.limit))
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    if (params.search?.trim())
      queryParams.append('search', params.search.trim())
    if (params.includeArchived) queryParams.append('includeArchived', 'true')
    if (params.status) queryParams.append('status', params.status)
    if (params.flaggedOnly) queryParams.append('flaggedOnly', 'true')

    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/scenarios${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  getById: async (scenarioId: string): Promise<ForecastScenario> => {
    return apiClient(`/member-resources/scenarios/${scenarioId}`, {
      method: 'GET',
    })
  },

  create: async (
    data: Omit<CreateForecastScenario, 'organizationId' | 'createdBy'>,
  ): Promise<ForecastScenario> => {
    return apiClient('/member-resources/scenarios', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    scenarioId: string,
    data: UpdateForecastScenario,
  ): Promise<ForecastScenario> => {
    return apiClient(`/member-resources/scenarios/${scenarioId}`, {
      method: 'PUT',
      body: data,
    })
  },

  delete: async (scenarioId: string): Promise<void> => {
    return apiClient(`/member-resources/scenarios/${scenarioId}`, {
      method: 'DELETE',
    })
  },

  calculate: async (
    scenarioId: string,
    params?: { forecastMonths?: number; granularity?: Granularity },
  ): Promise<ForecastScenario> => {
    return apiClient(`/member-resources/scenarios/${scenarioId}/calculate`, {
      method: 'POST',
      body: params || {},
    })
  },

  compare: async (
    scenarioIds: string[],
  ): Promise<{
    scenarios: ForecastScenario[]
    comparison: {
      metric: string
      values: { scenarioId: string; scenarioName: string; value: number }[]
    }[]
  }> => {
    return apiClient(
      `/member-resources/scenarios/compare?ids=${scenarioIds.join(',')}`,
      {
        method: 'GET',
      },
    )
  },

  baselineBreakdown: async (params?: {
    months?: number
    granularity?: Granularity
  }): Promise<{
    window: { startDate: string; endDate: string }
    byClient: import('@scrambl/shared').ScenarioBreakdownRow[]
    byProject: import('@scrambl/shared').ScenarioBreakdownRow[]
    byMember: import('@scrambl/shared').ScenarioBreakdownRow[]
  }> => {
    const queryParams = new URLSearchParams()
    if (params?.months) queryParams.append('months', String(params.months))
    if (params?.granularity)
      queryParams.append('granularity', params.granularity)
    const queryString = queryParams.toString()
    return apiClient(
      `/member-resources/scenarios/baseline-breakdown${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },
}

// PDF Export API
// Backend: /member-resources/export/{utilization,forecast,scenario}/pdf — all
// server-rendered documents, so they come back as raw PDF bytes rather than JSON.
export const pdfExportApi = {
  /**
   * Team utilization report for an explicit date range.
   *
   * `dateRangeLabel` is not a filter — the server prints it verbatim as the
   * report's period heading and uses it in the attachment filename, so it must
   * describe the same window `startDate`/`endDate` cover.
   */
  exportUtilizationPDF: async (params: {
    startDate: string
    endDate: string
    dateRangeLabel: string
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams()
    queryParams.append('startDate', params.startDate)
    queryParams.append('endDate', params.endDate)
    queryParams.append('dateRangeLabel', params.dateRangeLabel)

    return requestBlob(
      `/member-resources/export/utilization/pdf?${queryParams.toString()}`,
      { acceptType: 'application/pdf' },
    )
  },

  exportForecastPDF: async (params: {
    months: number
    granularity: string
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams()
    queryParams.append('months', params.months.toString())
    queryParams.append('granularity', params.granularity)

    return requestBlob(
      `/member-resources/export/forecast/pdf?${queryParams.toString()}`,
      { acceptType: 'application/pdf' },
    )
  },

  exportScenarioPDF: async (scenarioId: string): Promise<Blob> => {
    return requestBlob(
      `/member-resources/export/scenario/${scenarioId}/pdf`,
      { acceptType: 'application/pdf' },
    )
  },
}

// =====================
// Payroll Configuration API
// =====================

export const payrollConfigApi = {
  // Get or create payroll config for organization
  getConfig: async (): Promise<OrganizationPayrollConfig> => {
    return apiClient('/member-resources/payroll/config', { method: 'GET' })
  },

  // Update payroll config
  updateConfig: async (
    data: UpdatePayrollConfig,
  ): Promise<OrganizationPayrollConfig> => {
    return apiClient('/member-resources/payroll/config', {
      method: 'PUT',
      body: data,
    })
  },

  // Tax Items
  getTaxItems: async (): Promise<PayrollTaxItem[]> => {
    return apiClient('/member-resources/payroll/taxes', { method: 'GET' })
  },

  createTaxItem: async (
    data: CreatePayrollTaxItem,
  ): Promise<PayrollTaxItem> => {
    return apiClient('/member-resources/payroll/taxes', {
      method: 'POST',
      body: data,
    })
  },

  updateTaxItem: async (
    taxId: string,
    data: UpdatePayrollTaxItem,
  ): Promise<PayrollTaxItem> => {
    return apiClient(`/member-resources/payroll/taxes/${taxId}`, {
      method: 'PUT',
      body: data,
    })
  },

  deleteTaxItem: async (taxId: string): Promise<void> => {
    return apiClient(`/member-resources/payroll/taxes/${taxId}`, {
      method: 'DELETE',
    })
  },

  resetTaxesToDefaults: async (): Promise<{ message: string }> => {
    return apiClient('/member-resources/payroll/taxes/reset-defaults', {
      method: 'POST',
    })
  },

  // Benefit Templates
  getBenefitTemplates: async (): Promise<OrganizationBenefitTemplate[]> => {
    return apiClient('/member-resources/payroll/benefits', { method: 'GET' })
  },

  createBenefitTemplate: async (
    data: CreateBenefitTemplate,
  ): Promise<OrganizationBenefitTemplate> => {
    return apiClient('/member-resources/payroll/benefits', {
      method: 'POST',
      body: data,
    })
  },

  updateBenefitTemplate: async (
    benefitId: string,
    data: UpdateBenefitTemplate,
  ): Promise<OrganizationBenefitTemplate> => {
    return apiClient(`/member-resources/payroll/benefits/${benefitId}`, {
      method: 'PUT',
      body: data,
    })
  },

  deleteBenefitTemplate: async (benefitId: string): Promise<void> => {
    return apiClient(`/member-resources/payroll/benefits/${benefitId}`, {
      method: 'DELETE',
    })
  },

  resetBenefitsToDefaults: async (): Promise<{ message: string }> => {
    return apiClient('/member-resources/payroll/benefits/reset-defaults', {
      method: 'POST',
    })
  },
}

// =====================
// Member Compensation API
// =====================

export const compensationApi = {
  // Get all compensations for organization
  getAll: async (): Promise<MemberCompensation[]> => {
    return apiClient('/member-resources/compensations', { method: 'GET' })
  },

  // Get compensations for a member
  getByMember: async (memberId: string): Promise<MemberCompensation[]> => {
    return apiClient(`/member-resources/members/${memberId}/compensation`, {
      method: 'GET',
    })
  },

  // Get effective compensation for a member
  getEffective: async (
    memberId: string,
    date?: string,
  ): Promise<MemberCompensation | null> => {
    const params = date ? `?date=${date}` : ''
    return emptyResponseToNull(
      await apiClient<MemberCompensation | null>(
        `/member-resources/members/${memberId}/compensation/effective${params}`,
        { method: 'GET' },
      ),
    )
  },

  // Get compensation by ID
  getById: async (
    compensationId: string,
  ): Promise<MemberCompensation | null> => {
    return apiClient(`/member-resources/compensation/${compensationId}`, {
      method: 'GET',
    })
  },

  // Create compensation
  create: async (
    memberId: string,
    data: Omit<CreateMemberCompensation, 'memberId' | 'organizationId'>,
  ): Promise<MemberCompensation> => {
    return apiClient(`/member-resources/members/${memberId}/compensation`, {
      method: 'POST',
      body: data,
    })
  },

  // Update compensation
  update: async (
    compensationId: string,
    data: UpdateMemberCompensation,
  ): Promise<MemberCompensation> => {
    return apiClient(`/member-resources/compensation/${compensationId}`, {
      method: 'PUT',
      body: data,
    })
  },

  // Delete compensation
  delete: async (compensationId: string): Promise<void> => {
    return apiClient(`/member-resources/compensation/${compensationId}`, {
      method: 'DELETE',
    })
  },

  // Get breakdown
  getBreakdown: async (
    compensationId: string,
    payPeriod?: PayPeriodFrequency,
    ytdGross?: number,
  ): Promise<CompensationBreakdown> => {
    const params = new URLSearchParams()
    if (payPeriod) params.append('payPeriod', payPeriod)
    if (ytdGross !== undefined) params.append('ytdGross', ytdGross.toString())
    const queryString = params.toString()
    return apiClient(
      `/member-resources/compensation/${compensationId}/breakdown${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
    )
  },

  // Preview calculation (without saving)
  previewCalculation: async (data: {
    compensationType: CompensationType
    baseAmount: number
    hoursPerWeek?: number
    weeksPerYear?: number
    payPeriod?: PayPeriodFrequency
    ytdGross?: number
  }): Promise<CompensationBreakdown> => {
    return apiClient('/member-resources/compensation/calculate', {
      method: 'POST',
      body: data,
    })
  },

  // Member Benefits
  addBenefit: async (
    compensationId: string,
    data: CreateMemberBenefit,
  ): Promise<MemberBenefit> => {
    return apiClient(
      `/member-resources/compensation/${compensationId}/benefits`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  updateBenefit: async (
    benefitId: string,
    data: UpdateMemberBenefit,
  ): Promise<MemberBenefit> => {
    return apiClient(`/member-resources/compensation/benefits/${benefitId}`, {
      method: 'PUT',
      body: data,
    })
  },

  removeBenefit: async (benefitId: string): Promise<void> => {
    return apiClient(`/member-resources/compensation/benefits/${benefitId}`, {
      method: 'DELETE',
    })
  },

  // Tax Overrides
  addTaxOverride: async (
    compensationId: string,
    data: CreateMemberTaxOverride,
  ): Promise<MemberTaxOverride> => {
    return apiClient(
      `/member-resources/compensation/${compensationId}/tax-overrides`,
      {
        method: 'POST',
        body: data,
      },
    )
  },

  updateTaxOverride: async (
    overrideId: string,
    data: UpdateMemberTaxOverride,
  ): Promise<MemberTaxOverride> => {
    return apiClient(
      `/member-resources/compensation/tax-overrides/${overrideId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
  },

  removeTaxOverride: async (overrideId: string): Promise<void> => {
    return apiClient(
      `/member-resources/compensation/tax-overrides/${overrideId}`,
      { method: 'DELETE' },
    )
  },
}

// Job Role API
export const jobRoleApi = {
  getAll: async (): Promise<JobRole[]> => {
    return apiClient('/member-resources/roles', { method: 'GET' })
  },

  getConsolidatedList: async (params?: {
    search?: string
    status?: 'active' | 'inactive' | 'all'
    sortBy?: 'name' | 'memberCount' | 'rateCardCount' | 'activeProjectCount'
    sortDirection?: 'asc' | 'desc'
  }): Promise<ConsolidatedJobRoleListResponse> => {
    const query = new URLSearchParams()
    if (params?.search) query.append('search', params.search)
    if (params?.status) query.append('status', params.status)
    if (params?.sortBy) query.append('sortBy', params.sortBy)
    if (params?.sortDirection)
      query.append('sortDirection', params.sortDirection)

    return apiClient(
      `/member-resources/job-roles${query.toString() ? `?${query.toString()}` : ''}`,
      { method: 'GET' },
    )
  },

  getSummary: async (
    roleId: string,
  ): Promise<ConsolidatedJobRoleSummaryResponse> =>
    apiClient(`/member-resources/job-roles/${roleId}/summary`, {
      method: 'GET',
    }),

  getEffectiveRates: async (
    roleId: string,
    params?: { view?: 'project' | 'client'; entityId?: string },
  ): Promise<ConsolidatedJobRoleEffectiveRatesResponse> => {
    const query = new URLSearchParams()
    if (params?.view) query.append('view', params.view)
    if (params?.entityId) query.append('entityId', params.entityId)

    return apiClient(
      `/member-resources/job-roles/${roleId}/effective-rates${query.toString() ? `?${query.toString()}` : ''}`,
      { method: 'GET' },
    )
  },

  getAllocations: async (
    roleId: string,
  ): Promise<ConsolidatedJobRoleAllocationsResponse> =>
    apiClient(`/member-resources/job-roles/${roleId}/allocations`, {
      method: 'GET',
    }),

  getDiagnostics: async (
    roleId: string,
  ): Promise<ConsolidatedJobRoleDiagnosticsResponse> =>
    apiClient(`/member-resources/job-roles/${roleId}/diagnostics`, {
      method: 'GET',
    }),

  getById: async (roleId: string): Promise<JobRole> => {
    return apiClient(`/member-resources/roles/${roleId}`, { method: 'GET' })
  },

  create: async (
    data: Omit<CreateJobRole, 'organizationId'>,
  ): Promise<JobRole> => {
    return apiClient('/member-resources/roles', { method: 'POST', body: data })
  },

  update: async (roleId: string, data: UpdateJobRole): Promise<JobRole> => {
    return apiClient(`/member-resources/roles/${roleId}`, {
      method: 'PUT',
      body: data,
    })
  },

  delete: async (roleId: string): Promise<void> => {
    return apiClient(`/member-resources/roles/${roleId}`, { method: 'DELETE' })
  },
}

// Rate Card API
export const rateCardApi = {
  getAll: async (): Promise<RateCard[]> => {
    return apiClient('/member-resources/rate-cards', { method: 'GET' })
  },

  getById: async (rateCardId: string): Promise<RateCard> => {
    return apiClient(`/member-resources/rate-cards/${rateCardId}`, {
      method: 'GET',
    })
  },

  create: async (
    data: Omit<CreateRateCard, 'organizationId' | 'createdBy'>,
  ): Promise<RateCard> => {
    return apiClient('/member-resources/rate-cards', {
      method: 'POST',
      body: data,
    })
  },

  update: async (
    rateCardId: string,
    data: UpdateRateCard,
  ): Promise<RateCard> => {
    return apiClient(`/member-resources/rate-cards/${rateCardId}`, {
      method: 'PUT',
      body: data,
    })
  },

  delete: async (rateCardId: string): Promise<void> => {
    return apiClient(`/member-resources/rate-cards/${rateCardId}`, {
      method: 'DELETE',
    })
  },

  addEntry: async (
    rateCardId: string,
    data: Omit<CreateRateCardEntry, 'rateCardId'>,
    statusFilter?: string[],
  ): Promise<RateCard> => {
    return apiClient(`/member-resources/rate-cards/${rateCardId}/entries`, {
      method: 'POST',
      body: statusFilter ? { ...data, statusFilter } : data,
    })
  },

  updateEntry: async (
    entryId: string,
    data: { billRate?: number },
    statusFilter?: string[],
  ): Promise<void> => {
    return apiClient(`/member-resources/rate-card-entries/${entryId}`, {
      method: 'PUT',
      body: statusFilter ? { ...data, statusFilter } : data,
    })
  },

  deleteEntry: async (
    entryId: string,
    statusFilter?: string[],
  ): Promise<void> => {
    return apiClient(`/member-resources/rate-card-entries/${entryId}`, {
      method: 'DELETE',
      body: statusFilter ? { statusFilter } : undefined,
    })
  },

  clone: async (
    rateCardId: string,
    data: {
      name: string
      effectiveFrom: string
      effectiveTo?: string | null
      adjustmentPercent?: number
    },
  ): Promise<RateCard> => {
    return apiClient(`/member-resources/rate-cards/${rateCardId}/clone`, {
      method: 'POST',
      body: data,
    })
  },
}

// Effective Rate API (enhanced cascade)
export const effectiveRateApi = {
  getEffectiveRate: async (
    memberId: string,
    context?: {
      projectId?: string
      entityId?: string
      taskId?: string
      roleId?: string | null
      date?: string
    },
  ): Promise<EffectiveRateResult | null> => {
    const params = new URLSearchParams()
    if (context?.projectId) params.append('projectId', context.projectId)
    if (context?.entityId) params.append('entityId', context.entityId)
    if (context?.taskId) params.append('taskId', context.taskId)
    if (context?.roleId) params.append('roleId', context.roleId)
    if (context?.date) params.append('date', context.date)
    return emptyResponseToNull(
      await apiClient<EffectiveRateResult | null>(
        `/member-resources/members/${memberId}/rates/effective?${params.toString()}`,
        { method: 'GET' },
      ),
    )
  },
}

// Budget Category API
export const budgetCategoryApi = {
  getAll: async (): Promise<BudgetCategory[]> =>
    apiClient('/member-resources/budget/categories', { method: 'GET' }),
  create: async (
    data: Omit<CreateBudgetCategory, 'organizationId'>,
  ): Promise<BudgetCategory> =>
    apiClient('/member-resources/budget/categories', {
      method: 'POST',
      body: data,
    }),
  update: async (
    id: string,
    data: UpdateBudgetCategory,
  ): Promise<BudgetCategory> =>
    apiClient(`/member-resources/budget/categories/${id}`, {
      method: 'PUT',
      body: data,
    }),
  delete: async (id: string): Promise<void> =>
    apiClient(`/member-resources/budget/categories/${id}`, {
      method: 'DELETE',
    }),
}

// Budget Template API
export const budgetTemplateApi = {
  getAll: async (): Promise<BudgetTemplate[]> =>
    apiClient('/member-resources/budget/templates', { method: 'GET' }),
  getById: async (id: string): Promise<BudgetTemplate> =>
    apiClient(`/member-resources/budget/templates/${id}`, { method: 'GET' }),
  create: async (
    data: Omit<CreateBudgetTemplate, 'organizationId' | 'createdBy'>,
  ): Promise<BudgetTemplate> =>
    apiClient('/member-resources/budget/templates', {
      method: 'POST',
      body: data,
    }),
  update: async (
    id: string,
    data: UpdateBudgetTemplate,
  ): Promise<BudgetTemplate> =>
    apiClient(`/member-resources/budget/templates/${id}`, {
      method: 'PUT',
      body: data,
    }),
  delete: async (id: string): Promise<void> =>
    apiClient(`/member-resources/budget/templates/${id}`, { method: 'DELETE' }),
  addCategory: async (
    templateId: string,
    data: {
      categoryId: string
      defaultPercent?: number
      defaultHours?: number
    },
  ): Promise<BudgetTemplate> =>
    apiClient(`/member-resources/budget/templates/${templateId}/categories`, {
      method: 'POST',
      body: data,
    }),
  removeCategory: async (templateCategoryId: string): Promise<void> =>
    apiClient(
      `/member-resources/budget/template-categories/${templateCategoryId}`,
      { method: 'DELETE' },
    ),
}

// Budget Rule API
export const budgetRuleApi = {
  getAll: async (templateId?: string): Promise<BudgetRule[]> => {
    const params = templateId ? `?templateId=${templateId}` : ''
    return apiClient(`/member-resources/budget/rules${params}`, {
      method: 'GET',
    })
  },
  create: async (data: {
    templateId?: string
    ruleType: string
    action?: string
    threshold?: number
    operator?: string
    message?: string
    appliesToCategoryId?: string
    appliesToRoleId?: string
  }): Promise<BudgetRule> =>
    apiClient('/member-resources/budget/rules', { method: 'POST', body: data }),
  delete: async (id: string): Promise<void> =>
    apiClient(`/member-resources/budget/rules/${id}`, { method: 'DELETE' }),
}

// Project Budget API
export const projectBudgetApi = {
  get: async (projectId: string): Promise<ProjectBudget | null> =>
    apiClient(`/member-resources/projects/${projectId}/budget`, {
      method: 'GET',
    }),
  create: async (
    projectId: string,
    data: Omit<CreateProjectBudget, 'projectId' | 'createdBy'>,
  ): Promise<ProjectBudget> =>
    apiClient(`/member-resources/projects/${projectId}/budget`, {
      method: 'POST',
      body: data,
    }),
  update: async (
    projectId: string,
    data: {
      totalBudgetHours?: number | null
      totalBudgetCost?: number | null
      totalBudgetRevenue?: number | null
      marginTargetPercent?: number | null
    },
  ): Promise<ProjectBudget> =>
    apiClient(`/member-resources/projects/${projectId}/budget`, {
      method: 'PUT',
      body: data,
    }),
  getSummary: async (projectId: string) =>
    apiClient(`/member-resources/projects/${projectId}/budget/summary`, {
      method: 'GET',
    }),
  validate: async (projectId: string): Promise<BudgetValidationResult> =>
    apiClient(`/member-resources/projects/${projectId}/budget/validate`, {
      method: 'POST',
    }),
  // Phases
  getPhases: async (projectId: string): Promise<ProjectPhase[]> =>
    apiClient(`/member-resources/projects/${projectId}/phases`, {
      method: 'GET',
    }),
  createPhase: async (
    projectId: string,
    data: Omit<CreateProjectPhase, 'projectId'>,
  ): Promise<ProjectPhase> =>
    apiClient(`/member-resources/projects/${projectId}/phases`, {
      method: 'POST',
      body: data,
    }),
  updatePhase: async (
    phaseId: string,
    data: Partial<CreateProjectPhase>,
  ): Promise<ProjectPhase> =>
    apiClient(`/member-resources/phases/${phaseId}`, {
      method: 'PUT',
      body: data,
    }),
  deletePhase: async (phaseId: string): Promise<void> =>
    apiClient(`/member-resources/phases/${phaseId}`, { method: 'DELETE' }),
  // Exceptions
  getExceptions: async (projectId: string): Promise<BudgetExceptionRequest[]> =>
    apiClient(`/member-resources/projects/${projectId}/budget/exceptions`, {
      method: 'GET',
    }),
}

// Budget Exception API (org-wide for admin)
export const budgetExceptionApi = {
  getPending: async (): Promise<BudgetExceptionRequest[]> =>
    apiClient('/member-resources/budget/exceptions/pending', { method: 'GET' }),
  create: async (
    data: Omit<CreateBudgetExceptionRequest, 'organizationId' | 'createdBy'>,
  ): Promise<BudgetExceptionRequest> =>
    apiClient('/member-resources/budget/exceptions', {
      method: 'POST',
      body: data,
    }),
  approve: async (id: string): Promise<BudgetExceptionRequest> =>
    apiClient(`/member-resources/budget/exceptions/${id}/approve`, {
      method: 'POST',
    }),
  reject: async (
    id: string,
    rejectionReason?: string,
  ): Promise<BudgetExceptionRequest> =>
    apiClient(`/member-resources/budget/exceptions/${id}/reject`, {
      method: 'POST',
      body: { rejectionReason },
    }),
}

// Project Forecast API
export const projectForecastApi = {
  getForecast: async (projectId: string) =>
    apiClient(`/member-resources/projects/${projectId}/forecast`, {
      method: 'GET',
    }),
  takeSnapshot: async (projectId: string): Promise<ProjectForecastSnapshot> =>
    apiClient(`/member-resources/projects/${projectId}/forecast/snapshot`, {
      method: 'POST',
    }),
  getSnapshots: async (
    projectId: string,
    limit?: number,
  ): Promise<ProjectForecastSnapshot[]> => {
    const params = limit ? `?limit=${limit}` : ''
    return apiClient(
      `/member-resources/projects/${projectId}/forecast/snapshots${params}`,
      { method: 'GET' },
    )
  },
}

// Availability Check API
export const availabilityCheckApi = {
  check: async (
    memberId: string,
    params: {
      hoursPerWeek: number
      startDate: string
      endDate: string
    },
  ): Promise<MemberAvailabilityCheck> => {
    const query = new URLSearchParams({
      hoursPerWeek: String(params.hoursPerWeek),
      startDate: params.startDate,
      endDate: params.endDate,
    })
    return apiClient(
      `/member-resources/members/${memberId}/check-availability?${query}`,
      { method: 'GET' },
    )
  },
}

// Project Forecast Roll-Up & Alerts API
export const forecastRollUpApi = {
  getProjectRollUp: async (params?: {
    entityIds?: string[]
    projectIds?: string[]
    from?: string
    to?: string
    layers?: string[]
  }) => {
    const qp = new URLSearchParams()
    if (params?.entityIds?.length)
      qp.append('entityIds', params.entityIds.join(','))
    if (params?.projectIds?.length)
      qp.append('projectIds', params.projectIds.join(','))
    if (params?.from) qp.append('from', params.from)
    if (params?.to) qp.append('to', params.to)
    if (params?.layers?.length) qp.append('layers', params.layers.join(','))
    const qs = qp.toString()
    return apiClient(
      `/member-resources/forecast/project-rollup${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  },
  getProjectAlerts: async (projectId: string) =>
    apiClient(`/member-resources/projects/${projectId}/alerts`, {
      method: 'GET',
    }),
}

// Historical Trend API
export const trendApi = {
  getHistoricalTrends: async (params: {
    groupBy: 'client' | 'role' | 'projectType'
    startDate: string
    endDate: string
    periodType?: string
  }) => {
    const query = new URLSearchParams({
      groupBy: params.groupBy,
      startDate: params.startDate,
      endDate: params.endDate,
      ...(params.periodType ? { periodType: params.periodType } : {}),
    })
    return apiClient(`/member-resources/utilization/trends?${query}`, {
      method: 'GET',
    })
  },
}

// Allocation Constraint API
export const allocationConstraintApi = {
  getAll: async (): Promise<AllocationConstraint[]> =>
    apiClient('/member-resources/allocation-constraints', { method: 'GET' }),
  create: async (data: {
    constraintType: string
    threshold?: number
    operator?: string
    action?: string
    message?: string
    appliesToRoleId?: string
    appliesToEmploymentType?: string
  }): Promise<AllocationConstraint> =>
    apiClient('/member-resources/allocation-constraints', {
      method: 'POST',
      body: data,
    }),
  update: async (
    id: string,
    data: {
      threshold?: number
      operator?: string
      action?: string
      message?: string
      isActive?: boolean
    },
  ): Promise<AllocationConstraint> =>
    apiClient(`/member-resources/allocation-constraints/${id}`, {
      method: 'PUT',
      body: data,
    }),
  delete: async (id: string): Promise<void> =>
    apiClient(`/member-resources/allocation-constraints/${id}`, {
      method: 'DELETE',
    }),
  validate: async (data: {
    memberId: string
    hoursPerWeek?: number
    allocationPercent?: number
    projectId?: string
  }) =>
    apiClient('/member-resources/allocation-constraints/validate', {
      method: 'POST',
      body: data,
    }),
}

// Allocation Exception API
export const allocationExceptionApi = {
  getPending: async (): Promise<AllocationExceptionRequest[]> =>
    apiClient('/member-resources/allocation-exceptions/pending', {
      method: 'GET',
    }),
  getByMember: async (
    memberId: string,
  ): Promise<AllocationExceptionRequest[]> =>
    apiClient(`/member-resources/members/${memberId}/allocation-exceptions`, {
      method: 'GET',
    }),
  create: async (data: {
    memberId: string
    projectId?: string
    entityId?: string
    constraintId: string
    requestedHoursPerWeek?: number
    requestedAllocationPercent?: number
    requestedStartDate?: string
    requestedEndDate?: string
    reason: string
  }): Promise<AllocationExceptionRequest> =>
    apiClient('/member-resources/allocation-exceptions', {
      method: 'POST',
      body: data,
    }),
  approve: async (id: string): Promise<AllocationExceptionRequest> =>
    apiClient(`/member-resources/allocation-exceptions/${id}/approve`, {
      method: 'POST',
    }),
  reject: async (
    id: string,
    rejectionReason?: string,
  ): Promise<AllocationExceptionRequest> =>
    apiClient(`/member-resources/allocation-exceptions/${id}/reject`, {
      method: 'POST',
      body: { rejectionReason },
    }),
}

// Profitability & Realization API
// Money fields are null when the server masks them for the viewer.
export interface ProfitabilityByRoleRow {
  memberId: string
  memberName: string
  roleId: string | null
  roleName: string
  isW2: boolean
  resolvedFrom: string | null
  hours: number
  revenue: number | null
  cost: number | null
  margin: number | null
}

export interface ProfitabilityByRoleResult {
  pricingModel: 'TIME_AND_MATERIALS' | 'FIXED_FEE' | 'BLENDED'
  breakdown: ProfitabilityByRoleRow[]
  totals: {
    hours: number
    revenue: number | null
    cost: number | null
    margin: number | null
  }
  columnVisibility?: { billAmount: boolean; costAmount: boolean }
}

export interface RateTraceResponse {
  effectiveRate: number | null
  rateType: 'bill' | 'cost' | 'pay'
  currency: string
  resolvedFromLayer: number | null
  resolvedFromDescription: string | null
  sourceRecord: {
    id: string
    type: string
    label: string
    href: string | null
  } | null
  role: {
    id: string | null
    name: string | null
    source: 'explicit' | 'primary' | 'legacy' | 'none'
    sourceDescription: string
  }
  modifiers: Array<{
    type: string
    description: string
    preModifierRate: number | null
    percentage: number | null
  }>
  cascadeTrace: Array<{
    layer: number
    description: string
    result: number | null
    reason: string | null
    matched: boolean
    sourceRecordId: string | null
  }>
  resolvedAt: string | null
  currentCascadeWouldProduce: number | null
  rateIsStale: boolean
  warnings: Array<{
    code: string
    message: string
  }>
  permissions: {
    canViewBillData: boolean
    canViewCostData: boolean
    canViewPayData: boolean
  }
}

export interface RateHistoryResponse {
  items: Array<{
    id: string
    changedAt: string
    action: string
    summary: string
    entity: {
      id: string
      type: string
      label: string
    }
    actor: {
      id: string | null
      name: string
      email: string | null
    } | null
    previousValues: Record<string, unknown>
    newValues: Record<string, unknown>
    fieldsChanged: string[]
  }>
  permissions: {
    canViewBillData: boolean
    canViewCostData: boolean
    canViewPayData: boolean
  }
}

export interface RateSimulationResponse {
  changeDescription: string
  affectedCount: {
    members: number
    projects: number
    allocations: number
  }
  affectedRates: Array<{
    memberId: string
    memberName: string
    projectId: string | null
    projectName: string | null
    entityName: string | null
    roleId: string | null
    roleName: string | null
    currentRate: number | null
    newRate: number | null
    difference: number | null
    differencePercent: number | null
    newRateSource: string | null
    weeklyImpact: number | null
  }>
  financialImpact: {
    weeklyRevenueChange: number | null
    monthlyRevenueChange: number | null
    affectedProjectBudgets: Array<{
      projectId: string
      projectName: string
      currentPlannedCost: number | null
      projectedPlannedCost: number | null
      costDifference: number | null
    }>
  }
  largeImpact: {
    isTruncated: boolean
    displayedRows: number
    totalRows: number
    downloadToken: string | null
  }
  noImpactReason: string | null
  permissions: {
    canViewBillData: boolean
    canViewCostData: boolean
    canViewPayData: boolean
  }
}

export interface ConsolidatedJobRoleListResponse {
  items: Array<{
    roleId: string
    roleName: string
    status: 'active' | 'inactive'
    memberCount: number
    rateCardCount: number
    activeProjectCount: number
    coverageStatus: 'full_coverage' | 'gaps_detected'
    averageEffectiveBillRate: number | null
  }>
}

export interface ConsolidatedJobRoleSummaryResponse {
  role: {
    id: string
    name: string
    description: string | null
    status: 'active' | 'inactive'
  }
  usageSummary: {
    memberCount: number
    rateCardCount: number
    activeProjectCount: number
    coverageGapCount: number
    averageEffectiveBillRate: number | null
  }
  permissions: {
    canViewBillData: boolean
    canViewCostData: boolean
    canViewPayData: boolean
  }
}

export interface ConsolidatedJobRoleEffectiveRatesResponse {
  items: Array<{
    projectId: string | null
    projectName: string | null
    entityId: string | null
    entityName: string | null
    rateSource: string
    effectiveBillRate: number | null
    fallbackSource: string | null
    isRoleSpecific: boolean
    variesAcrossMembers: boolean
    memberCount: number
    hoursLogged: number | null
    revenue: number | null
    activeAllocationCount: number
  }>
  permissions: {
    canViewBillData: boolean
    canViewCostData: boolean
    canViewPayData: boolean
  }
}

export interface ConsolidatedJobRoleAllocationsResponse {
  items: Array<{
    allocationId: string
    memberId: string
    memberName: string
    projectId: string | null
    projectName: string | null
    entityId: string | null
    entityName: string | null
    startDate: string
    endDate: string
    hoursPerWeek: number | null
    status: string
    hasCoverageGap: boolean
  }>
}

export interface ConsolidatedJobRoleDiagnosticsResponse {
  coverageStatus: 'full_coverage' | 'gaps_detected'
  coverageGaps: Array<{
    memberId: string
    memberName: string
    projectId: string | null
    projectName: string | null
    entityName: string | null
    currentFallbackSource: string | null
    estimatedRoleSpecificRate: number | null
  }>
  fallbackUsage: {
    lastCalculatedAt: string | null
    totalEntries: number
    fallbackPercent: number
    trend: 'increasing' | 'stable' | 'decreasing'
    byMember: Array<{
      memberId: string
      memberName: string
      entryCount: number
    }>
  }
  rateVariance: {
    spreadPercent: number | null
    minRate: number | null
    maxRate: number | null
  }
  permissions: {
    canViewBillData: boolean
    canViewCostData: boolean
    canViewPayData: boolean
  }
}

export const profitabilityApi = {
  getProjectProfitability: async (projectId: string) =>
    apiClient(`/member-resources/projects/${projectId}/profitability`, {
      method: 'GET',
    }),
  getProjectProfitabilityByRole: async (
    projectId: string,
  ): Promise<ProfitabilityByRoleResult> =>
    apiClient(`/member-resources/projects/${projectId}/profitability/by-role`, {
      method: 'GET',
    }),
  getProjectProfitabilityByRoleEnhanced: async (projectId: string) =>
    apiClient(
      `/member-resources/projects/${projectId}/profitability/by-role-enhanced`,
      {
        method: 'GET',
      },
    ),
  getOrgRealization: async (startDate: string, endDate: string) =>
    apiClient(
      `/member-resources/realization?startDate=${startDate}&endDate=${endDate}`,
      { method: 'GET' },
    ),
  getRetainerUsage: async (contractId: string) =>
    apiClient(`/member-resources/contracts/${contractId}/retainer-usage`, {
      method: 'GET',
    }),
}

export const rateTraceApi = {
  resolveTrace: async (params: {
    memberId?: string
    projectId?: string
    entityId?: string
    roleId?: string | null
    taskId?: string
    timeEntryId?: string
    rateType?: 'bill' | 'cost' | 'pay'
    asOfDate?: string
  }): Promise<RateTraceResponse> => {
    const query = new URLSearchParams()
    if (params.memberId) query.append('memberId', params.memberId)
    if (params.projectId) query.append('projectId', params.projectId)
    if (params.entityId) query.append('entityId', params.entityId)
    if (params.roleId) query.append('roleId', params.roleId)
    if (params.taskId) query.append('taskId', params.taskId)
    if (params.timeEntryId) query.append('timeEntryId', params.timeEntryId)
    if (params.rateType) query.append('rateType', params.rateType)
    if (params.asOfDate) query.append('asOfDate', params.asOfDate)

    return apiClient(
      `/member-resources/rates/resolve-trace${query.toString() ? `?${query.toString()}` : ''}`,
      { method: 'GET' },
    )
  },
}

export const rateHistoryApi = {
  getHistory: async (params: {
    memberId?: string
    projectId?: string
    entityId?: string
    roleId?: string | null
    taskId?: string
    timeEntryId?: string
    rateType?: 'bill' | 'cost' | 'pay'
  }): Promise<RateHistoryResponse> => {
    const query = new URLSearchParams()
    if (params.memberId) query.append('memberId', params.memberId)
    if (params.projectId) query.append('projectId', params.projectId)
    if (params.entityId) query.append('entityId', params.entityId)
    if (params.roleId) query.append('roleId', params.roleId)
    if (params.taskId) query.append('taskId', params.taskId)
    if (params.timeEntryId) query.append('timeEntryId', params.timeEntryId)
    if (params.rateType) query.append('rateType', params.rateType)

    return apiClient(
      `/member-resources/rates/history${query.toString() ? `?${query.toString()}` : ''}`,
      { method: 'GET' },
    )
  },
}

export const rateSimulationApi = {
  simulate: async (body: {
    changeType:
      | 'rate_card_entry_update'
      | 'rate_card_assignment_update'
      | 'member_rate_update'
      | 'member_default_update'
      | 'org_default_update'
      | 'project_discount_update'
      | 'client_discount_update'
      | 'project_rate_type_update'
      | 'compensation_update'
      | 'role_assignment_update'
      | 'rate_card_deactivation'
    recordId: string
    proposedValues: Record<string, unknown>
    includeFullImpact?: boolean
  }): Promise<RateSimulationResponse> =>
    apiClient('/member-resources/rates/simulate', {
      method: 'POST',
      body,
    }),
}

export const projectWorkspaceApi = {
  getStaffing: async (
    projectId: string,
    params?: {
      statusTier?: string
      periodStart?: string
      periodEnd?: string
      includeHistorical?: boolean
    },
  ): Promise<ProjectStaffingResponse> => {
    const query = new URLSearchParams()
    if (params?.statusTier) query.append('statusTier', params.statusTier)
    if (params?.periodStart) query.append('periodStart', params.periodStart)
    if (params?.periodEnd) query.append('periodEnd', params.periodEnd)
    if (params?.includeHistorical != null) {
      query.append('includeHistorical', String(params.includeHistorical))
    }

    return apiClient(
      `/member-resources/projects/${projectId}/staffing${query.toString() ? `?${query.toString()}` : ''}`,
      { method: 'GET' },
    )
  },
  getTimeEntries: async (
    projectId: string,
    params?: {
      statusTier?: string
      startDate?: string
      endDate?: string
      memberIds?: string[]
      roleIds?: string[]
      workItemType?: 'PROJECT' | 'TASK' | 'TASK_GROUP'
      workItemIds?: string[]
      authorizationId?: string
      billable?: boolean
      status?: string[]
      groupBy?: 'member' | 'week' | 'workItem' | 'role'
      page?: number
      limit?: number
      sortBy?:
        | 'entryDate'
        | 'member'
        | 'role'
        | 'moduleName'
        | 'duration'
        | 'status'
      sortDirection?: 'asc' | 'desc'
    },
  ): Promise<ProjectTimeEntriesResponse> => {
    const query = new URLSearchParams()
    if (params?.statusTier) query.append('statusTier', params.statusTier)
    if (params?.startDate) query.append('startDate', params.startDate)
    if (params?.endDate) query.append('endDate', params.endDate)
    params?.memberIds?.forEach(id => query.append('memberIds', id))
    params?.roleIds?.forEach(id => query.append('roleIds', id))
    if (params?.workItemType) query.append('workItemType', params.workItemType)
    params?.workItemIds?.forEach(id => query.append('workItemIds', id))
    if (params?.authorizationId)
      query.append('authorizationId', params.authorizationId)
    if (params?.billable != null)
      query.append('billable', String(params.billable))
    params?.status?.forEach(value => query.append('status', value))
    if (params?.groupBy) query.append('groupBy', params.groupBy)
    if (params?.page) query.append('page', String(params.page))
    if (params?.limit) query.append('limit', String(params.limit))
    if (params?.sortBy) query.append('sortBy', params.sortBy)
    if (params?.sortDirection)
      query.append('sortDirection', params.sortDirection)

    return apiClient(
      `/member-resources/projects/${projectId}/time-entries${query.toString() ? `?${query.toString()}` : ''}`,
      { method: 'GET' },
    )
  },
  getOverviewSummary: async (
    projectId: string,
  ): Promise<ProjectOverviewSummaryResponse> =>
    apiClient(`/member-resources/projects/${projectId}/overview-summary`, {
      method: 'GET',
    }),
}

// Advanced Analytics API (Phase 6)
export const advancedAnalyticsApi = {
  getBenchReport: async (startDate: string, endDate: string) =>
    apiClient(
      `/member-resources/bench-report?startDate=${startDate}&endDate=${endDate}`,
      { method: 'GET' },
    ),
  getCapacityHeatMap: async (startDate: string, weeks?: number) => {
    const params = new URLSearchParams({ startDate })
    if (weeks) params.append('weeks', String(weeks))
    return apiClient(`/member-resources/capacity-heatmap?${params}`, {
      method: 'GET',
    })
  },
  // Overhead
  getOverheadCategories: async () =>
    apiClient('/member-resources/overhead-categories', { method: 'GET' }),
  createOverheadCategory: async (data: Record<string, unknown>) =>
    apiClient('/member-resources/overhead-categories', {
      method: 'POST',
      body: data,
    }),
  updateOverheadCategory: async (id: string, data: Record<string, unknown>) =>
    apiClient(`/member-resources/overhead-categories/${id}`, {
      method: 'PUT',
      body: data,
    }),
  deleteOverheadCategory: async (id: string): Promise<void> =>
    apiClient(`/member-resources/overhead-categories/${id}`, {
      method: 'DELETE',
    }),
  getOverheadAllocation: async () =>
    apiClient('/member-resources/overhead-allocation', { method: 'GET' }),
}

export interface MemberProjectAssignment {
  projectId: string
  projectName: string
  entityId: string | null
  entityName: string | null
  rateCardId: string | null
  rateCardName: string | null
  rateCardSource: 'project' | 'entity' | null
  accessRole: string
  /** Where this project's billing roles come from for this member. */
  billingSource: 'project' | 'entity' | 'none'
  /** True when the project member is in inherit mode. */
  billingInheritsFromEntity: boolean
  jobRoles: Array<{
    roleId: string
    roleName: string
    billRate: number | null
  }>
}

export interface MemberEntityAssignment {
  entityId: string
  entityName: string
  rateCardId: string | null
  rateCardName: string | null
  jobRoles: Array<{
    roleId: string
    roleName: string
    billRate: number | null
  }>
}

/**
 * Per-row resolved rate from the bill cascade. `billSource` names the cascade
 * level that produced the bill rate (e.g., `CLIENT_PERSON` = client-level
 * override, `CLIENT_RATE_CARD` = rate card entry). `payCostSource` is the
 * origin of pay/cost (compensation vs manual member rate vs nothing). A null
 * `effectiveFrom` means the rate came from a rate card or default, which don't
 * carry a per-member effective date.
 */
export interface ResolvedBillingRate {
  payRate: number | null
  billRate: number | null
  costRate: number | null
  billSource:
    | 'TASK_RATE'
    | 'PROJECT_PERSON'
    | 'PROJECT_RATE_CARD'
    | 'CLIENT_PERSON'
    | 'CLIENT_RATE_CARD'
    | 'PERSON_DEFAULT'
    | 'ROLE_DEFAULT'
    | 'ORG_DEFAULT'
    | null
  payCostSource: 'COMPENSATION' | 'MEMBER_RATE' | 'NONE' | null
  effectiveFrom: string | null
  effectiveTo: string | null
}

export interface EntityBillingMember {
  memberId: string
  userId: string
  name: string
  email: string | null
  image: string | null
  roles: Array<{
    roleId: string
    roleName: string
    billRate: number | null
    resolvedRate: ResolvedBillingRate | null
  }>
}

export interface ProjectBillingMember {
  memberId: string | null
  userId: string
  name: string
  email: string | null
  image: string | null
  accessRole: string
  billingInheritsFromEntity: boolean
  billingSource: 'project' | 'entity' | 'none'
  roles: Array<{
    roleId: string
    roleName: string
    billRate: number | null
    resolvedRate: ResolvedBillingRate | null
  }>
}

/**
 * Client-level rate overrides that exist on a member but don't correspond to
 * any roster row (either the member has no roster entry, or the rate is
 * role-scoped to a role the member doesn't have on the roster). Rendered in
 * the amber "Rates without membership" section on the Rates panel.
 */
export interface EntityOrphanClientRate {
  rateId: string
  memberId: string
  userId: string
  name: string
  email: string | null
  image: string | null
  roleId: string | null
  roleName: string | null
  payRate: number | null
  billRate: number | null
  costRate: number | null
  effectiveFrom: string
  effectiveTo: string | null
  hasRosterEntry: boolean
}

export interface EntityBillingImpactPreview {
  inheritingProjects: Array<{
    projectId: string
    projectName: string
    entityName?: string | null
    currentRoleIds: string[]
    newRoleIds: string[]
    gainedRoleIds: string[]
    lostRoleIds: string[]
  }>
  overrideProjects: Array<{
    projectId: string
    projectName: string
    entityName?: string | null
  }>
  affectedDraftEntries: {
    count: number
    examples: Array<{
      id: string
      description: string | null
      entryDate: string
    }>
  }
}

// Member Role Assignment API
// Backend: /member-resources/members/:memberId/roles
export const memberRoleApi = {
  getByMember: async (memberId: string): Promise<MemberOrganizationRole[]> => {
    return apiClient(`/member-resources/members/${memberId}/roles`, {
      method: 'GET',
    })
  },

  assign: async (
    memberId: string,
    data: AssignMemberRole,
  ): Promise<MemberOrganizationRole> => {
    return apiClient(`/member-resources/members/${memberId}/roles`, {
      method: 'POST',
      body: data,
    })
  },

  remove: async (
    memberId: string,
    roleId: string,
    options?: { deactivateRoleRates?: boolean },
  ): Promise<{
    warning?: string
    affectedRoleRateCount: number
    deactivatedRoleRateCount: number
  }> => {
    return apiClient(`/member-resources/members/${memberId}/roles/${roleId}`, {
      method: 'DELETE',
      body: options,
    })
  },

  setPrimary: async (
    memberId: string,
    roleId: string,
  ): Promise<MemberOrganizationRole> => {
    return apiClient(
      `/member-resources/members/${memberId}/roles/${roleId}/primary`,
      { method: 'PUT' },
    )
  },

  // T15 (U4): set this role's share of the member's weekly capacity (0..1).
  setCapacityFraction: async (
    memberId: string,
    roleId: string,
    capacityFraction: number,
  ): Promise<MemberOrganizationRole> => {
    return apiClient(
      `/member-resources/members/${memberId}/roles/${roleId}/capacity`,
      { method: 'PUT', body: { capacityFraction } },
    )
  },

  getProjectAssignments: async (
    memberId: string,
  ): Promise<MemberProjectAssignment[]> => {
    return apiClient(
      `/member-resources/members/${memberId}/project-assignments`,
      { method: 'GET' },
    )
  },

  getEntityAssignments: async (
    memberId: string,
  ): Promise<MemberEntityAssignment[]> => {
    return apiClient(
      `/member-resources/members/${memberId}/entity-assignments`,
      { method: 'GET' },
    )
  },

  getAvailable: async (params: {
    memberId: string
    projectId?: string | null
    entityId?: string | null
    /** ISO date (yyyy-MM-dd) — used with requireRate to filter rates active on this date. */
    date?: string | null
    /** When true, only returns roles with an exact (member, role, project) MemberRate active on `date`. */
    requireRate?: boolean
  }): Promise<MemberOrganizationRole[]> => {
    const searchParams = new URLSearchParams()
    if (params.projectId) searchParams.append('projectId', params.projectId)
    if (params.entityId) searchParams.append('entityId', params.entityId)
    if (params.date) searchParams.append('date', params.date)
    if (params.requireRate) searchParams.append('requireRate', 'true')
    const qs = searchParams.toString()
    return apiClient(
      `/member-resources/members/${params.memberId}/roles/available${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  },
}

// Entity Job Role Scoping API
// Backend: /entities/:entityId/job-role-selections
export const entityJobRoleSelectionApi = {
  get: async (entityId: string): Promise<EntityJobRoleSelection[]> => {
    return apiClient(`/entities/${entityId}/job-role-selections`, {
      method: 'GET',
    })
  },

  set: async (
    entityId: string,
    data: SetEntityJobRoleSelections & { confirmed?: boolean },
  ): Promise<{
    selections?: EntityJobRoleSelection[]
    confirmationRequired?: boolean
    affectedRoles?: Array<{
      roleId: string
      roleName: string
      entryCount: number
    }>
  }> => {
    return apiClient(`/entities/${entityId}/job-role-selections`, {
      method: 'PUT',
      body: data,
    })
  },
}

// Project Member Job Role Scoping API
// Backend: /projects/:projectId/members/:memberId/job-roles
export const projectMemberJobRoleApi = {
  get: async (
    projectId: string,
    memberId: string,
  ): Promise<ProjectMemberJobRole[]> => {
    return apiClient(`/projects/${projectId}/members/${memberId}/job-roles`, {
      method: 'GET',
    })
  },

  set: async (
    projectId: string,
    memberId: string,
    data: SetProjectMemberJobRoles,
  ): Promise<ProjectMemberJobRole[]> => {
    return apiClient(`/projects/${projectId}/members/${memberId}/job-roles`, {
      method: 'PUT',
      body: data,
    })
  },

  setBillingInheritance: async (
    projectId: string,
    memberId: string,
    data: { inherits: boolean; seedFromInherited?: boolean },
  ): Promise<{ success: boolean }> => {
    return apiClient(
      `/projects/${projectId}/members/${memberId}/billing-inheritance`,
      {
        method: 'PUT',
        body: data,
      },
    )
  },
}

// Project Billing Members API (resolved project-side roster)
// Backend: /projects/:projectId/members/billing-members
export const projectBillingMemberApi = {
  list: async (projectId: string): Promise<ProjectBillingMember[]> => {
    return apiClient(`/projects/${projectId}/members/billing-members`, {
      method: 'GET',
    })
  },
}

// Entity Billing Members API (per-member client-level billing roster)
// Backend: /entities/:entityId/billing-members
export const entityBillingMemberApi = {
  list: async (entityId: string): Promise<EntityBillingMember[]> => {
    return apiClient(`/entities/${entityId}/billing-members`, { method: 'GET' })
  },

  /**
   * Client-level `MemberRate` overrides whose (member, role) isn't covered by
   * a roster entry. Used to render the amber "Rates without membership"
   * section alongside the main roster table.
   */
  listOrphanRates: async (
    entityId: string,
  ): Promise<EntityOrphanClientRate[]> => {
    return apiClient(`/entities/${entityId}/billing-members/orphan-rates`, {
      method: 'GET',
    })
  },

  preview: async (
    entityId: string,
    memberId: string,
    roleIds: string[],
  ): Promise<EntityBillingImpactPreview> => {
    return apiClient(
      `/entities/${entityId}/billing-members/${memberId}/preview`,
      {
        method: 'POST',
        body: { roleIds },
      },
    )
  },

  set: async (
    entityId: string,
    memberId: string,
    roleIds: string[],
    confirmed = true,
  ): Promise<{ success: boolean }> => {
    return apiClient(`/entities/${entityId}/billing-members/${memberId}`, {
      method: 'PUT',
      body: { roleIds, confirmed },
    })
  },
}

// Rate Backfill API
// Backend: /member-resources/rates/backfill

export interface BackfillScopeOptions {
  dateFrom?: string
  dateTo?: string
  projectId?: string
}

export interface BackfillPreviewDay {
  entryDate: string
  entryCount: number
  hours: number
  billableHours: number
  moduleLabels: string[]
}

export interface BackfillPreviewPerson {
  userId: string
  memberId: string | null
  name: string
  image: string | null
  entryCount: number
  hours: number
  billableHours: number
  earliestDate: string | null
  latestDate: string | null
  days: BackfillPreviewDay[]
}

export interface BackfillPreviewScope {
  totalEntries: number
  totalHours: number
  billableHours: number
  currentBillableAmount: number
  skippedApprovedCount: number
  pricingKeyCount: number
  people: BackfillPreviewPerson[]
}

export interface BackfillPricedGroup {
  userId: string
  entryDate: string
  moduleType: string | null
  moduleId: string | null
  roleId: string | null
  entryCount: number
  hours: number
  billableHours: number
  outcome: 'RESOLVED' | 'NO_RATE' | 'NO_MEMBER'
  resolvedFrom: string | null
  resolvedRoleId: string | null
  billRate: number | null
  payRate: number | null
  costRate: number | null
  projectedBillable: number
  projectedCost: number
  projectedPay: number
}

export interface BackfillPricedChunk {
  priced: BackfillPricedGroup[]
  nextCursor: number | null
  remaining: number
  breakdown: Record<string, number>
}

export const rateBackfillApi = {
  dryRun: async (options: {
    dateFrom?: string
    dateTo?: string
    projectId?: string
  }): Promise<{ totalEntries: number; estimatedDurationMs: number }> => {
    return apiClient('/member-resources/rates/backfill', {
      method: 'POST',
      body: { ...options, dryRun: true },
    })
  },

  run: async (options: {
    dateFrom?: string
    dateTo?: string
    projectId?: string
    batchSize?: number
  }): Promise<{
    totalEntries: number
    resolved: number
    unchanged: number
    skipped: number
    failed: Array<{ entryId: string; reason: string }>
    /** Counts by cascade layer for the rates that were written. */
    breakdown: Record<string, number>
    durationMs: number
  }> => {
    return apiClient('/member-resources/rates/backfill', {
      method: 'POST',
      body: { ...options, dryRun: false },
    })
  },

  /**
   * What the backfill will touch, grouped by person then date. Exact and
   * instant — no rates resolved, so nothing here is sampled or estimated.
   */
  preview: async (
    options: BackfillScopeOptions,
  ): Promise<BackfillPreviewScope> => {
    return apiClient('/member-resources/rates/backfill/preview', {
      method: 'POST',
      body: options,
    })
  },

  /**
   * Price one slice of the preview. Walk `nextCursor` until it comes back null
   * to price the whole scope.
   */
  priceChunk: async (
    options: BackfillScopeOptions & { cursor?: number; limit?: number },
  ): Promise<BackfillPricedChunk> => {
    return apiClient('/member-resources/rates/backfill/preview/price', {
      method: 'POST',
      body: options,
    })
  },

  /**
   * Org-wide rate-honesty counts: entries with no bill rate at all, and entries
   * priced from the date-blind org default rather than a historical rate.
   */
  unresolvedCount: async (): Promise<{
    unresolvedBillableCount: number
    earliestEntryDate: string | null
    orgDefaultCount: number
    /** The org-default subset that is APPROVED, so no rate change can re-price it. */
    orgDefaultApprovedCount: number
  }> => {
    return apiClient('/member-resources/rates/unresolved-count')
  },
}

export interface HistoricalUtilizationRebuildResult {
  utilization:
    | {
        rebuilt: true
        startDate: string
        endDate: string
      }
    | {
        rebuilt: false
      }
  earnings: {
    periodsProcessed: number
    runs: Array<{
      periodStart: string
      periodEnd: string
      summary: unknown
    }>
  }
}

export const historicalUtilizationMaintenanceApi = {
  recalculateHistoricalUtilization:
    async (): Promise<HistoricalUtilizationRebuildResult> => {
      return apiClient(
        '/member-resources/maintenance/rebuild-utilization-and-earnings',
        { method: 'POST', body: {} },
      )
    },
}

// Workload API
// Backend: GET /member-resources/members/:memberId/workload
// POST /member-resources/allocations/:id/clear-review-flag

export type MemberWorkloadSummary = SharedWorkloadSummary

export interface MemberWorkloadTrendPoint {
  weekStartingOn: string
  utilPct: number
  marginPct: number | null
  inOverTargetStreak: boolean
}

// Plan 9b/9c: Team Insights types
export interface TeamInsightsMemberRow {
  memberId: string
  memberName: string
  memberEmail: string
  memberImage: string | null
  department: string | null
  jobTitle: string | null
  primaryRole: { id: string; name: string } | null
  burnoutRisk: { level: string; streakWeeks: number } | null
  revenueAttainmentPct: number | null
  billableUtilization: number
  totalUtilization: number
  targetUtilization: number
  periodStart: string | null
  totalHours: number
  billableHours: number
  allocatedHours: number
  availableCapacity: number
  calculatedAt: string | null
  calculationTrigger: string | null
  isStale: boolean
  // Plan 9c T14-c
  weightedMarginPct: number | null
  marginIncludesTotalCost: boolean
  marginIndicator: 'green' | 'yellow' | 'red' | null
  topProject: {
    projectId: string
    projectName: string
    entityName?: string | null
    hours: number
    marginPct: number | null
  } | null
}

export interface TeamInsightsSummary {
  primaryMetric: string
  secondaryMetric: string
  burnoutCount: { elevated: number; high: number; total: number }
  teamRevenueAttainment: number | null
  revenueCards: {
    tier: string
    revenue: number
    profit: number
    footnote: string
  } | null
  teamWeightedMarginPct: number | null // Plan 9c T15-c
  marginAtRiskHours: number // Plan 9c T15-c (0 when none at risk)
  marginThreshold: number // org-configured threshold for UI coloring
  freshness: {
    lastCalculatedAt: string | null
    isStale: boolean
    stalenessThresholdHours: number
  }
  // Team-wide aggregates (server-computed so the cards/bars survive table pagination)
  memberCount: number
  avgBillableUtilization: number
  avgTotalUtilization: number
  workloadDistribution: {
    under: number
    healthy: number
    approaching: number
    over: number
  }
  burnoutDistribution: {
    noRisk: number
    watch: number
    elevated: number
    high: number
  }
}

/** Server-paginated page of insights members. */
export interface TeamInsightsMembersPage {
  items: TeamInsightsMemberRow[]
  total: number
  page: number
  pageSize: number
}

// Plan 9c T16: Margin breakdown by project
export interface MarginBreakdownProject {
  projectId: string | null
  projectName: string
  entityName?: string | null
  hours: number
  totalRevenue: number
  totalCost: number
  marginPct: number | null
  marginTrend: Array<{ weekStart: string; marginPct: number | null }>
}

export interface MarginBreakdownResponse {
  memberId: string
  weightedMarginPct: number | null
  billableUtilizationPct: number
  rows: MarginBreakdownProject[]
}

export interface MemberWorkloadProject {
  projectId: string
  projectName: string
  entityName?: string | null
  hours: number
  billRate: number | null
  costRate: number | null
  marginPct: number | null
  marginIndicator: 'green' | 'yellow' | 'red' | null
  marginTrend: number[]
}

export interface MemberWorkloadAllocation {
  id: string
  memberId: string
  projectId: string | null
  projectName: string | null
  entityId: string | null
  entityName?: string | null
  dealId: string | null
  roleId: string | null
  roleName: string | null
  hoursPerWeek: number | null
  startDate: string | null
  endDate: string | null
  status: string
  flaggedForReview: boolean
  targetType: WorkloadTargetType
  targetId: string | null
  targetName: string | null
  parentEntityName: string | null
  allocationPercent: number | null
  allocationType: string | null
  priority: number
  notes: string | null
}

export interface MemberWorkloadResponse {
  summary: MemberWorkloadSummary
  trend: MemberWorkloadTrendPoint[]
  weeks: WorkloadWeekPoint[]
  availability: WorkloadAvailabilitySummary
  projects: MemberWorkloadProject[]
  allocations: MemberWorkloadAllocation[]
}

export const memberWorkloadApi = {
  getWorkload: async (
    memberId: string,
    params?: { period?: string },
  ): Promise<MemberWorkloadResponse> => {
    const query = new URLSearchParams()
    if (params?.period) query.append('period', params.period)
    const qs = query.toString()
    return apiClient(
      `/member-resources/members/${memberId}/workload${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    )
  },

  clearReviewFlag: async (
    allocationId: string,
  ): Promise<{ id: string; flaggedForReview: boolean }> => {
    return apiClient(
      `/member-resources/allocations/${allocationId}/clear-review-flag`,
      { method: 'POST', body: {} },
    )
  },
}

// Plan 9b/9c: Team Insights API
export const teamInsightsApi = {
  getAll: async (): Promise<{
    summary: TeamInsightsSummary
    members: TeamInsightsMemberRow[]
  }> => {
    return apiClient('/member-resources/insights')
  },
  getMembers: async (params?: {
    sortBy?:
      | 'burnoutRisk'
      | 'billableUtilization'
      | 'totalUtilization'
      | 'weightedMargin'
    startDate?: string
    endDate?: string
  }): Promise<TeamInsightsMemberRow[]> => {
    const query = new URLSearchParams()
    if (params?.sortBy) query.append('sortBy', params.sortBy)
    if (params?.startDate) query.append('startDate', params.startDate)
    if (params?.endDate) query.append('endDate', params.endDate)
    const qs = query.toString()
    return apiClient(`/member-resources/insights/members${qs ? `?${qs}` : ''}`)
  },
  getMembersPaged: async (params: {
    sortBy?:
      | 'burnoutRisk'
      | 'billableUtilization'
      | 'totalUtilization'
      | 'weightedMargin'
    groupBy?: 'none' | 'department' | 'role'
    search?: string
    page?: number
    pageSize?: number
    startDate?: string
    endDate?: string
  }): Promise<TeamInsightsMembersPage> => {
    const query = new URLSearchParams()
    if (params.sortBy) query.append('sortBy', params.sortBy)
    if (params.groupBy) query.append('groupBy', params.groupBy)
    if (params.search) query.append('search', params.search)
    if (params.page != null) query.append('page', String(params.page))
    if (params.pageSize != null)
      query.append('pageSize', String(params.pageSize))
    if (params.startDate) query.append('startDate', params.startDate)
    if (params.endDate) query.append('endDate', params.endDate)
    const qs = query.toString()
    return apiClient(
      `/member-resources/insights/members/paged${qs ? `?${qs}` : ''}`,
    )
  },
  getSummary: async (): Promise<TeamInsightsSummary> => {
    return apiClient('/member-resources/insights/summary')
  },
  // Plan 9c T16: per-member margin breakdown by project
  getMarginBreakdown: async (
    memberId: string,
    params?: { periodStart?: string; periodEnd?: string },
  ): Promise<MarginBreakdownResponse> => {
    const query = new URLSearchParams()
    if (params?.periodStart) query.append('periodStart', params.periodStart)
    if (params?.periodEnd) query.append('periodEnd', params.periodEnd)
    const qs = query.toString()
    return apiClient(
      `/member-resources/insights/members/${memberId}/margin-breakdown${qs ? `?${qs}` : ''}`,
    )
  },
  // T27: Trigger recalculation for specific stale members before re-export
  refreshUtilization: async (memberIds: string[]): Promise<void> => {
    await apiClient('/member-resources/utilization/calculate', {
      method: 'POST',
      body: { memberIds },
    })
  },
  // Plan 9 T17: Team Insights PDF export with staleness check
  exportPdf: async (params: {
    primaryMetric?: string
    secondaryMetric?: string
    acknowledgeStale?: boolean
  }): Promise<{ blob: Blob; staleMemberIds?: string[] }> => {
    // 409 is part of this endpoint's contract (stale utilization data awaiting
    // acknowledgement), not a failure — hence the raw-response variant.
    const response = await requestBlobResponse(
      '/member-resources/insights/export/pdf',
      {
        method: 'POST',
        acceptType: 'application/pdf',
        body: params,
      },
    )
    if (response.status === 409) {
      const json = await response.json()
      return { blob: new Blob(), staleMemberIds: json.staleMemberIds ?? [] }
    }
    if (!response.ok) {
      throw new Error(`PDF export failed: ${response.statusText}`)
    }
    const blob = await response.blob()
    return { blob }
  },
}

// Export all APIs as a single object for convenience
export const memberResourcesApi = {
  rates: memberRateApi,
  allocations: memberAllocationApi,
  skills: memberSkillApi,
  utilization: memberUtilizationApi,
  earnings: memberEarningsApi,
  forecast: forecastApi,
  scenarios: scenarioApi,
  pdfExport: pdfExportApi,
  schedule: scheduleApi,
  payrollConfig: payrollConfigApi,
  compensation: compensationApi,
  jobRoles: jobRoleApi,
  rateCards: rateCardApi,
  effectiveRate: effectiveRateApi,
  rateTrace: rateTraceApi,
  rateHistory: rateHistoryApi,
  rateSimulation: rateSimulationApi,
  budgetCategories: budgetCategoryApi,
  budgetTemplates: budgetTemplateApi,
  budgetRules: budgetRuleApi,
  projectBudget: projectBudgetApi,
  budgetExceptions: budgetExceptionApi,
  projectForecast: projectForecastApi,
  availabilityCheck: availabilityCheckApi,
  forecastRollUp: forecastRollUpApi,
  trends: trendApi,
  allocationConstraints: allocationConstraintApi,
  allocationExceptions: allocationExceptionApi,
  projectWorkspace: projectWorkspaceApi,
  profitability: profitabilityApi,
  analytics: advancedAnalyticsApi,
  memberRoles: memberRoleApi,
  entityRoleSelections: entityJobRoleSelectionApi,
  projectMemberRoles: projectMemberJobRoleApi,
  historicalUtilizationMaintenance: historicalUtilizationMaintenanceApi,
  rateBackfill: rateBackfillApi,
  workload: memberWorkloadApi,
}
